import { format as formatDate, formatDistance, formatDuration, formatRelative, intervalToDuration, isDate, Locale, setDefaultOptions } from "date-fns";
import { ar, de, enUS, es, fr, hi, it, ja, ko, ru, vi, zhCN } from "date-fns/locale";
import { f7 } from "framework7-svelte";
import i18next, { FormatterModule } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import { default as enTr } from "../locales/en.json";
import { Event, recordEvent } from './analytics';
import { scheduleNotifications } from './notifications';
import { APP_NAME, getUser } from './settings';

// Grab all JSON files EXCEPT the en.json already statically imported
const localeFiles = import.meta.glob([
  '../locales/**/*.json',
  '!../locales/en.json' // ignore the en file as it's already statically imported
]);

// Dictionary / constants words not to translate
const constants = {
  app: {
    name: APP_NAME
  }
}

export const languages = {
  en: 'English',
  ru: 'русский',
  es: 'Español',
  fr: 'Français',
  it: 'Italiano',
  de: 'Deutsch',
  zh: '中文',
  ja: '日本語',
  ko: '한국어',
  vi: 'Tiếng Việt',
  hi: 'हिन्दी',
  ar: 'العربية', // TODO: handle right to left lang
}

const codeToDateLocale: Record<string, Locale> = {
  'en': enUS,
  'ru': ru,
  'es': es,
  'fr': fr,
  'it': it,
  'de': de,
  'zh': zhCN,
  'ja': ja,
  'ko': ko,
  'vi': vi,
  'hi': hi,
  'ar': ar,
}

const LANG_STORAGE_KEY = 'coherence-lang';
const DEFAULT_NS = 'translation';

// Create a dedicated i18next instance
export const i18nInstance = i18next.createInstance();

/**
 * Get a translation based on a key
 * @param key the identifier of the translation (in JSON)
 * @param options 
 * @returns 
 */
export const translate = (key: string, options?: any): string => { // eslint-disable-line  @typescript-eslint/no-explicit-any
  let translation = i18nInstance.t(key, options) as string;
  // undefined when not initialized yet
  if (translation == null || typeof translation !== 'string') {
    translation = `Undefined: ${key}`;
  }
  return translation.replace(/\s+/g, ' ') // normalize spaces. i.e. for empty variables;
}

/**
 * Get the raw translation value
 * @param key 
 * @param options 
 * @returns the raw translation value or values as object or array
 */
export const getTranslationValue = (key: string, options?: any): string | object | [] => { // eslint-disable-line  @typescript-eslint/no-explicit-any
  return i18nInstance.t(key, { returnObjects: true, ...options });
}

// export a simple interface for translation
export const translateWithUsername = (key: string, options: object = {}): string => {
  const user = getUser();
  const translation = translate(key, { ...user, ...options }); // add user info
  // remove multiple whitespaces into a single, useful when interpolation var is empty like username
  return translation.replace(/\s+/g, ' ').trim();
}

/**
 * UNSAFE, alow for html tags
 * 
 * @param key 
 * @param options 
 * @returns 
 */
export const trUnescaped = (key: string, options: object = {}): string => {
  return i18nInstance.getResource(i18nInstance.language, '', key, options);
}

/**
 * Split a translated string based on variable name
 * 
 * @param key 
 * @param options 
 * @returns a 2 string array guaranteed
 */
export const splitTranslation = (key: string, varname: string, options: object = {}): string[] => {
  options = { ...options }
  options[varname] = '|||';
  const translated = translate(key, options);
  return translated.split('|||').map((s) => s.trim());
}

export const getLanguage = (): string => {
  if (!i18nInstance.language) return 'en';
  return i18nInstance.language.substring(0, 2).toLowerCase(); // first 2 letters
}

export const setLanguage = async (lang: string) => {
  window.localStorage.setItem(LANG_STORAGE_KEY, lang);
  await i18nInstance.changeLanguage(lang);
  // change date format
  updateDateLang();
  // record lang change as an event
  recordEvent(Event.JoinGroup, { group_id: lang });
  // re-schedule notifications with the new language
  await scheduleNotifications(f7, false);
}


export const updateDateLang = () => {
  const langCode = getLanguage();
  let locale = enUS;
  // check that lang exists or use EN
  if (langCode in codeToDateLocale) {
    locale = codeToDateLocale[langCode];
  }
  // change date format globally
  setDefaultOptions({ locale: locale });
}

const DateFormatter: FormatterModule = {
  type: 'formatter',
  format: (value: any, format: any, lng: any, options: any) => { // eslint-disable-line  @typescript-eslint/no-explicit-any
    if (isDate(value)) {
      if (format === undefined) return value;
      let locale = enUS; // check that lang exists or use EN
      if (lng && lng in codeToDateLocale) {
        locale = codeToDateLocale[lng];
      }
      if (format === "short")
        return formatDate(value, "P", { locale });
      if (format === "long")
        return formatDate(value, "PPPP", { locale });
      if (format === "relative")
        return formatRelative(value, new Date(), { locale });
      if (format === "ago")
        return formatDistance(value, new Date(), { locale, addSuffix: true });
      if (format === "duration") {
        const duration = intervalToDuration({ start: new Date(), end: value });
        return formatDuration(duration);
      }

      return formatDate(value, format, { locale });
    }

    return value;
  },
  init() { },
  add() { },
  addCached() { }
};

export const i18nInit = async () => {
  return i18nInstance
    .use(LanguageDetector)
    // custom formatter plugin to replace the legacy `interpolation.format` function
    .use(DateFormatter)
    .use(resourcesToBackend((language: string, namespace: string) => // lazy-loading JSON files from a 'locales' folder:
    {
      console.log('Loading locale', namespace === DEFAULT_NS ? `../locales/${language}.json` : `../locales/${namespace}/${language}.json`);
      if (language === 'en' && namespace === DEFAULT_NS) {
        return Promise.resolve(enTr);
      }
      const path = namespace === DEFAULT_NS ? `../locales/${language}.json` : `../locales/${namespace}/${language}.json`;
      const loader = localeFiles[path];
      if (!loader) {
        return Promise.reject(`Locale file not found: ${path}`);
      }
      return loader();
    }))
    .init({
      // detection: options,
      detection: { order: ['localStorage', 'navigator'], lookupLocalStorage: LANG_STORAGE_KEY },
      // resources: resources,
      load: 'languageOnly', //  'en' instead of en-US
      defaultNS: DEFAULT_NS,
      debug: false,
      supportedLngs: Object.keys(languages),
      fallbackLng: {
        'de-CH': ['fr', 'it'], //French and Italian are also spoken in Switzerland
        'es': ['fr'],
        'default': ['en']
      },
      // use formatter plugin instead of legacy interpolation.format
      interpolation: {},
      // load en and default values as fallback
      resources: {
        en: { translation: { ...constants, ...enTr } }, // append constants to default language
      },
      // 2. Crucial: allows loading extra namespaces for 'en' from the backend
      partialBundledLanguages: true,
      showSupportNotice: false,
    },
      // after init callback
      function () {
        updateDateLang(); // Sets the defaults for all date functions
      });
}


/**
 * Get a welcome message based on hour of day
 * 
 * @param hourOfDay null for a generyic "Hey {username}"
 *
 * @returns 
 */
export function getWelcomeMessage(hourOfDay: number | null = null): string {
  if (hourOfDay != null) {
    let timeOfDay = 'morning';
    if (hourOfDay >= 12 && hourOfDay < 18) {
      timeOfDay = 'afternoon';
    } else if (hourOfDay <= 3 || hourOfDay >= 18) {
      timeOfDay = 'evening';
    }
    return translateWithUsername('home.welcome_message', { context: timeOfDay });
  }
  return translateWithUsername('home.welcome_greeting');
}

/**
 *  Function to safely get the first letter/character of a string respecting unicode
 *  
 * @param str
 * @param normalize Get the first letter, strips diacritics away, returning the base Latin character where applicable.
 * @returns 
 **/
export function getFirstLetter(str: string, normalize: boolean = true): string {
  if (!str) return '';
  // Array.from splits by Unicode code points, preventing broken characters
  if (normalize) {
    str = str.normalize('NFD');
  }
  return Array.from(str)[0];
}

/**
 * Formats a duration in seconds into a localized, narrow string format (e.g., "5s", "٥ ث").
 *
 * @param {number} seconds - The number of seconds to format.
 * @returns {string} The formatted string combining the number and its narrow localized unit symbol.
 */
export const formatSecondsShort = (seconds: number): string => {
  const lang = getLanguage();
  try {
    return new Intl.NumberFormat(lang, {
      style: "unit",
      unit: "second",
      unitDisplay: "narrow", // <-- Forces 's' or 'ث' natively
    }).format(seconds);
  } catch (e) {  // Fallback: just return the number with 's' or 'ث' based on language
    return `${seconds}${getFirstLetter(tr('common.second', { count: seconds }))}`;
  }
};


// shorter version
export const tr = translate;
export default translate;